import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import Navbar from './Navbar';
import VaultoLogo from './VaultoLogo';
import Footer from './Footer';
import { trackAuth, trackError } from '../utils/analytics';
import { captureAndStoreUTM, getAcquisitionData, clearUTMParams } from '../utils/utmCapture';
// Browser detection removed - no longer blocking users in in-app browsers

const Register = () => {
  const { t } = useTranslation();
  const { register: registerUser, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [affiliateCode, setAffiliateCode] = useState(null);
  const [subIds, setSubIds] = useState({ sub1: null, sub2: null, sub3: null });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const watchPassword = watch('password');

  useEffect(() => {
    // Check for referral code in URL parameter
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
    }
    
    // Check for affiliate code in URL parameter or localStorage
    const affCode = searchParams.get('aff') || localStorage.getItem('vaulto_affiliate_code');
    if (affCode) {
      setAffiliateCode(affCode);
      // Store in localStorage if from URL (for persistence across page refreshes)
      if (searchParams.get('aff')) {
        localStorage.setItem('vaulto_affiliate_code', affCode);
      }
    }
    
    // Capture sub-ID tracking parameters from URL
    const sub1 = searchParams.get('sub1') || localStorage.getItem('vaulto_sub1');
    const sub2 = searchParams.get('sub2') || localStorage.getItem('vaulto_sub2');
    const sub3 = searchParams.get('sub3') || localStorage.getItem('vaulto_sub3');
    
    if (sub1 || sub2 || sub3) {
      setSubIds({ sub1, sub2, sub3 });
      // Store in localStorage for persistence
      if (searchParams.get('sub1')) localStorage.setItem('vaulto_sub1', sub1);
      if (searchParams.get('sub2')) localStorage.setItem('vaulto_sub2', sub2);
      if (searchParams.get('sub3')) localStorage.setItem('vaulto_sub3', sub3);
    }
    
    // Capture and store UTM parameters from URL
    captureAndStoreUTM();
  }, [searchParams]);

  const onSubmit = async (data) => {
    const { confirmPassword, ...userData } = data;
    // Add referral code if present
    if (referralCode) {
      userData.referral_code = referralCode;
    }
    
    // Add affiliate code if present
    if (affiliateCode) {
      userData.affiliate_code = affiliateCode;
    }
    
    // Add sub-ID tracking parameters if present
    if (subIds.sub1) userData.sub1 = subIds.sub1;
    if (subIds.sub2) userData.sub2 = subIds.sub2;
    if (subIds.sub3) userData.sub3 = subIds.sub3;
    
    // Get acquisition data (UTM params, referrer domain, acquisition source)
    const acquisitionData = getAcquisitionData();
    
    // Add UTM parameters if available
    if (Object.keys(acquisitionData.utmParams).length > 0) {
      Object.assign(userData, acquisitionData.utmParams);
    }
    
    // Add referrer domain and acquisition source
    if (acquisitionData.referrerDomain) {
      userData.referrer_domain = acquisitionData.referrerDomain;
    }
    userData.acquisition_source = acquisitionData.acquisitionSource;
    
    const result = await registerUser(userData);
    if (result.success) {
      // Clear UTM parameters, affiliate code, and sub-IDs after successful registration
      clearUTMParams();
      localStorage.removeItem('vaulto_affiliate_code');
      localStorage.removeItem('vaulto_sub1');
      localStorage.removeItem('vaulto_sub2');
      localStorage.removeItem('vaulto_sub3');
      // Track successful signup
      trackAuth.signup('email');
      
      // Check for redirect parameter first (e.g., from affiliates page)
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
        return;
      }
      
      // Check if wizard mode is pending (user came from homepage with ?wizard=true)
      const wizardModePending = localStorage.getItem('wizard_mode_pending');
      if (wizardModePending === 'true') {
        localStorage.removeItem('wizard_mode_pending');
        sessionStorage.setItem('wizard_mode_active', 'true');
        navigate('/dashboard', { replace: true });
      } else {
      navigate('/dashboard');
      }
    } else {
      // Track failed signup attempt
      trackError.authError('signup_failed', result.error || 'Registration failed');
    }
  };

  const handleGoogleSignup = async () => {
    try {
      // NOTE: Signup tracking happens in OAuthCallback.js when new_user=true
      // Don't track here - user hasn't completed signup yet
      
      // Store redirect parameter for after OAuth callback
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        localStorage.setItem('oauth_redirect', redirectTo);
      }
      
      // Get acquisition data (UTM params, referrer domain, acquisition source)
      const acquisitionData = getAcquisitionData();
      
      // Build query string with UTM parameters and acquisition data
      const queryParams = new URLSearchParams();
      if (acquisitionData.utmParams.utm_source) queryParams.append('utm_source', acquisitionData.utmParams.utm_source);
      if (acquisitionData.utmParams.utm_medium) queryParams.append('utm_medium', acquisitionData.utmParams.utm_medium);
      if (acquisitionData.utmParams.utm_campaign) queryParams.append('utm_campaign', acquisitionData.utmParams.utm_campaign);
      if (acquisitionData.utmParams.utm_term) queryParams.append('utm_term', acquisitionData.utmParams.utm_term);
      if (acquisitionData.utmParams.utm_content) queryParams.append('utm_content', acquisitionData.utmParams.utm_content);
      if (acquisitionData.referrerDomain) queryParams.append('referrer_domain', acquisitionData.referrerDomain);
      queryParams.append('acquisition_source', acquisitionData.acquisitionSource);
      
      // Add affiliate code if present
      if (affiliateCode) queryParams.append('aff', affiliateCode);
      
      // Add sub-ID tracking parameters if present
      if (subIds.sub1) queryParams.append('sub1', subIds.sub1);
      if (subIds.sub2) queryParams.append('sub2', subIds.sub2);
      if (subIds.sub3) queryParams.append('sub3', subIds.sub3);
      
      // Add signup=true to request Drive scopes during sign-up
      queryParams.append('signup', 'true');
      
      // Get Google auth URL from backend with UTM parameters and acquisition data
      const url = `${process.env.REACT_APP_API_URL}/auth/google/login${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.auth_url) {
        // Clear UTM parameters before redirect (they're encoded in OAuth state)
        clearUTMParams();
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      } else {
        throw new Error('Failed to get Google auth URL');
      }
    } catch (error) {
      console.error('Google signup error:', error);
      trackError.authError('google_signup_failed', error.message);
      // You might want to show an error toast here
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 to-pink-900/50"></div>
      <div className="absolute top-0 left-0 right-0 h-full">
        <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-32 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-10 left-1/3 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>
      
      <Navbar />
      <div className="relative flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Glassmorphism container */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="text-center">
              {/* Logo */}
              <div className="mx-auto mb-6">
                <VaultoLogo className="h-16 w-auto mx-auto" showText={true} />
              </div>
              
              <h2 className="text-3xl font-extrabold text-white mb-2">
                {t('register.title')}
              </h2>
              <p className="text-gray-300 text-sm">
                {t('register.subtitle')}
              </p>
              
              {/* Referral message */}
              {referralCode && (
                <div className="mt-4 bg-green-500/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-green-400/30">
                  <p className="text-green-200 text-sm">
                    {t('register.referralMessage')}
                  </p>
                </div>
              )}
              
              {/* Affiliate message */}
              {/* Trust indicators */}
              <div className="mt-4 flex justify-center space-x-4">
                <div className="inline-flex items-center space-x-1 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs">
                  <svg className="h-3 w-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('home.hero.trust.freePlan')}</span>
                </div>
                <div className="inline-flex items-center space-x-1 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-white text-xs">
                  <svg className="h-3 w-3 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <span>{t('login.secureLogin')}</span>
                </div>
              </div>
            </div>
        
            <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {/* Google Sign Up Button - Top */}
              <div className="mb-6">
                <button
                  type="button"
                  onClick={handleGoogleSignup}
                  className="w-full flex items-center justify-center px-4 py-3 border border-white/20 rounded-xl text-white bg-white/10 hover:bg-white/20 transition-colors shadow-lg backdrop-blur-sm"
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {t('register.googleSignUp')}
                </button>
              </div>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 text-gray-400 bg-transparent">{t('register.orContinue')}</span>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('register.fullName.label')}
                  </label>
                  <input
                    id="fullName"
                    type="text"
                    autoComplete="name"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.full_name ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder={t('register.fullName.placeholder')}
                    {...register('full_name', {
                      required: t('register.fullName.required'),
                      minLength: {
                        value: 2,
                        message: t('register.fullName.required'),
                      },
                    })}
                  />
                  {errors.full_name && (
                    <p className="mt-2 text-sm text-red-400">{errors.full_name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('register.email.label')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.email ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder={t('register.email.placeholder')}
                    {...register('email', {
                      required: t('register.email.required'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('register.email.invalid'),
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-400">{errors.email.message}</p>
                  )}
                </div>
                
                <div className="relative">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('register.password.label')}
                  </label>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 pr-12 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.password ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder={t('register.password.placeholder')}
                    {...register('password', {
                      required: t('register.password.required'),
                      minLength: {
                        value: 6,
                        message: t('register.password.minLength'),
                      },
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
                  )}
                </div>

                <div className="relative">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-2">
                    {t('register.confirmPassword.label')}
                  </label>
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`appearance-none rounded-xl relative block w-full px-4 py-3 pr-12 border-0 bg-white/10 backdrop-blur-sm ${
                      errors.confirmPassword ? 'ring-2 ring-red-400' : 'ring-1 ring-white/20'
                    } placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-pink-400 transition-all duration-200 sm:text-sm`}
                    placeholder={t('register.confirmPassword.placeholder')}
                    {...register('confirmPassword', {
                      required: t('register.confirmPassword.required'),
                      validate: (value) =>
                        value === watchPassword || t('register.confirmPassword.match'),
                    })}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 top-6 pr-4 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-white transition-colors" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-2 text-sm text-red-400">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-xl text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200"
                >
                  {loading ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : null}
                  {loading ? t('register.creating') : t('register.createAccount')}
                </button>
              </div>

              <div className="text-center pt-4 border-t border-white/10">
                <p className="text-sm text-gray-300">
                  {t('register.haveAccount')}{' '}
                  <Link
                    to="/login"
                    className="font-semibold text-pink-400 hover:text-pink-300 transition-colors"
                  >
                    {t('register.signIn')}
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
      
      <Footer variant="dark" />
    </div>
  );
};

export default Register; 